import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { Storyboard } from "pages/media/types/";

export const useStoryboard = ({ path }: { path?: string }) => {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ["storyboard", path], [path]);

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<Storyboard> => {
      const response = await fetch(path); // we know path is not undefined because of the enabled condition
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      return response.json();
    },
    enabled: !!path,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const updateSection = useCallback(
    (sectionId: string, updates: Partial<Storyboard["sections"][number]>) => {
      queryClient.setQueryData<Storyboard>(queryKey, (old) =>
        old
          ? {
              ...old,
              sections: old.sections.map((s) =>
                s.id === sectionId ? { ...s, ...updates } : s
              ),
            }
          : old
      );
    },
    [queryClient, queryKey]
  );

  // Reorder sections to match the given id order. Ids absent from the list
  // keep their relative order after the listed ones (defensive against a
  // partial id list during a refetch).
  const reorderSections = useCallback(
    (orderedSectionIds: string[]) => {
      queryClient.setQueryData<Storyboard>(queryKey, (old) => {
        if (!old) return old;
        const rank = new Map(orderedSectionIds.map((id, index) => [id, index]));
        const sections = [...old.sections].sort(
          (a, b) =>
            (rank.get(a.id) ?? Number.MAX_SAFE_INTEGER) -
            (rank.get(b.id) ?? Number.MAX_SAFE_INTEGER)
        );
        return { ...old, sections };
      });
    },
    [queryClient, queryKey]
  );

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    isSuccess: query.isSuccess,
    error: query.error,
    refetch: query.refetch,
    updateSection,
    reorderSections,
  };
};
